import { logger } from '@/services/logger';
import { useEffect, useState } from 'react';

export const useDevice = () => {
    const [permissionStatus, setPermissionStatus] = useState<PermissionStatus['state'] | null>(null);
    const [mediaDevices, setMediaDevices] = useState<MediaStream>();
    const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
    const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
    const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>('');
    const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');

    const checkCameraPermission = async (): Promise<void> => {
        try {
            const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
            setPermissionStatus(permissionStatus.state);

            permissionStatus.onchange = () => {
                setPermissionStatus(permissionStatus.state);
            };
        } catch (error) {
            logger.error('Error checking camera permission:', error);
        }
    };

    const requestDeviceAccess = async (): Promise<void> => {
        try {
            const media = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            setMediaDevices(media);
        } catch (error: any) {
            logger.error('Error accessing the camera:', error);
            if (error.name === 'NotAllowedError') {
                // Camera access denied by the user
            } else if (error.name === 'NotFoundError') {
                // No camera found on the device
            }
        }
    };

    const disableDevice = async (): Promise<void> => {
        try {
            mediaDevices?.getTracks().forEach(track => track.stop());
            setVideoDevices([]);
            setAudioDevices([]);
            setSelectedVideoDevice('');
            setSelectedAudioDevice('');
            setPermissionStatus(null);
        } catch (error) {
            logger.error('Error disabling camera:', error);
        }
    };

    useEffect(() => {
        checkCameraPermission();
        const getDevices = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoInputDevices = devices.filter(device => device.kind === 'videoinput');
                const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
                setVideoDevices(videoInputDevices);
                setAudioDevices(audioInputDevices);
                setSelectedVideoDevice(videoInputDevices[0]?.deviceId || '');
                setSelectedAudioDevice(audioInputDevices[0]?.deviceId || '');
            } catch (err) {
                logger.error('Error accessing media devices:', err);
            }
        };

        getDevices();
    }, [permissionStatus]);

    return {
        videoDevices,
        audioDevices,
        selectedVideoDevice,
        selectedAudioDevice,
        setSelectedVideoDevice,
        setSelectedAudioDevice,
        permissionStatus,
        requestDeviceAccess,
        disableDevice,
    };
};
